/**
 * Organization form fields and their reader. See lead-form.tsx for why the two
 * are co-located and why the reader gates on `form.has()`.
 */

import type { Organization } from '@erp/contracts'
import { decimal, int, text } from '../forms'
import { optionsFrom, useIndustries, useOwnerOptions } from '../status-context'
import { Field, Select, TextInput } from '../ui'
import { LookupSelect } from './lookup-select'

export function readOrganizationForm(form: FormData): Record<string, unknown> {
  const fields: Record<string, unknown> = {}
  const put = (key: string, value: unknown) => {
    if (form.has(key)) fields[key] = value
  }

  put('name', text(form, 'name') ?? '')
  put('website', text(form, 'website'))
  put('industryId', text(form, 'industryId'))
  put('noOfEmployees', int(form, 'noOfEmployees'))
  put('annualRevenue', decimal(form, 'annualRevenue'))
  put('currency', text(form, 'currency'))
  put('addressLine1', text(form, 'addressLine1'))
  put('addressLine2', text(form, 'addressLine2'))
  put('city', text(form, 'city'))
  put('state', text(form, 'state'))
  put('postalCode', text(form, 'postalCode'))
  put('country', text(form, 'country'))
  put('ownerId', text(form, 'ownerId'))

  return fields
}

export function OrganizationFields({
  organization,
  issues,
}: {
  organization?: Organization | undefined
  issues: Record<string, string[]>
}) {
  const industries = useIndustries()
  const owners = useOwnerOptions(organization?.ownerId ?? null)

  return (
    <>
      <fieldset className="ax-fieldset">
        <legend>Company</legend>
        <Field label="Name" error={issues['name']}>
          <TextInput name="name" defaultValue={organization?.name} required />
        </Field>
        <Field label="Website" error={issues['website']}>
          <TextInput
            name="website"
            type="url"
            placeholder="https://"
            defaultValue={organization?.website}
          />
        </Field>
        <Field label="Industry" error={issues['industryId']}>
          <LookupSelect
            name="industryId"
            kind="industry"
            items={industries}
            defaultValue={organization?.industryId ?? null}
          />
        </Field>
        <Field label="Employees" error={issues['noOfEmployees']}>
          <TextInput
            name="noOfEmployees"
            type="number"
            defaultValue={organization?.noOfEmployees?.toString()}
          />
        </Field>
        <Field label="Annual revenue" error={issues['annualRevenue']}>
          <TextInput
            name="annualRevenue"
            type="number"
            defaultValue={organization?.annualRevenue?.toString()}
          />
        </Field>
        {/* Required whenever revenue is set — a database constraint, mirrored here. */}
        <Field label="Currency" error={issues['currency']}>
          <TextInput name="currency" placeholder="USD" defaultValue={organization?.currency} />
        </Field>
        <Field label="Owner" error={issues['ownerId']}>
          <Select name="ownerId" value={organization?.ownerId ?? ''} options={owners} />
        </Field>
      </fieldset>

      <fieldset className="ax-fieldset">
        <legend>Address</legend>
        <Field label="Line 1" error={issues['addressLine1']}>
          <TextInput name="addressLine1" defaultValue={organization?.addressLine1} />
        </Field>
        <Field label="Line 2" error={issues['addressLine2']}>
          <TextInput name="addressLine2" defaultValue={organization?.addressLine2} />
        </Field>
        <Field label="City" error={issues['city']}>
          <TextInput name="city" defaultValue={organization?.city} />
        </Field>
        <Field label="State" error={issues['state']}>
          <TextInput name="state" defaultValue={organization?.state} />
        </Field>
        <Field label="Postal code" error={issues['postalCode']}>
          <TextInput name="postalCode" defaultValue={organization?.postalCode} />
        </Field>
        <Field label="Country" error={issues['country']}>
          <TextInput name="country" defaultValue={organization?.country} />
        </Field>
      </fieldset>
    </>
  )
}
